/**
 * Monitor — what your regulators published, what it touches, and what needs
 * your word.
 *
 * The order is the argument. Whether the watch ran sits in the page header
 * (`SourcesStatus`); then the firm's frameworks as Linear's initiatives
 * table — where the heat is — each row narrowing the list below it; then the
 * publications as one list grouped the way Linear groups issues by status —
 * open findings, needs decision, relevant, then the not-applicable piles
 * folded — a tab per group and the finer filters (source, regime, type,
 * stage) behind one Filter button. Every count leads to the rows it counts.
 *
 * Scope is derived, never typed: the Regimes page is the watch list. See
 * docs/architecture/monitor.md.
 */
import {
  CalendarClockIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  FileTextIcon,
  LandmarkIcon,
  LayersIcon,
  ListFilterIcon,
  SearchIcon,
  XIcon,
} from "lucide-react";
import { Dish } from "@lucasmarkes/hairline/react";
import { Link, useLocation, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";

import { HairlineArt } from "~/components/ui/hairline-art";
import { cn } from "~/lib/utils";
import { safeExternalHref } from "../../../lib/safeExternalHref";
import { useFrameworks, useGovernanceFrameworks, useMonitors } from "../../../state/governance";
import { UNAVAILABLE_MESSAGES, useOrgProfile } from "../../../state/orgProfile";
import {
  policyDisplayName,
  usePolicies,
  useRegulatoryUpdates,
  type PublicationApplicability,
  type PublicationKind,
  type PublicationStage,
  type RegulatoryUpdate,
} from "../../../state/policies";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { ChartTooltipContent } from "../../ui/chart";
import { LabelTag } from "../../ui/label-chip";
import { Popover, PopoverClose, PopoverPopup, PopoverTrigger } from "../../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../ui/select";
import { Sheet, SheetFooter, SheetHeader, SheetPanel, SheetPopup, SheetTitle } from "../../ui/sheet";
import { FrameworksTable } from "../FrameworksTable";
import { PageHeader, SegmentedTabs } from "../PageHeader";
import { RejectDialog } from "../policy/RejectDialog";
import { useRegulationOrg } from "../useRegulationOrg";
import { filedUnder, type RegimeCoverage } from "./frameworkRollup";
import { DeciderAvatar, ImpactIcon, StatusIcon } from "./linearGlyphs";
import { publicationIdFromHash } from "./publicationLink";
import {
  DAY,
  formatDate,
  groupPublications,
  impactLevel,
  IMPACT_LABELS,
  publicationState,
  type PublicationGroup,
  type PublicationState,
} from "./publications";
import { SourcesStatus } from "./SourcesStatus";

/* ------------------------------------------------------------------ */
/* Vocabulary                                                          */
/* ------------------------------------------------------------------ */

export const KIND_LABELS: Record<PublicationKind, string> = {
  consultation: "Consultation",
  guidance: "Guidance",
  rule: "Rule",
  enforcement: "Enforcement",
  notice: "Notice",
  other: "Publication",
};

export const STAGE_LABELS: Record<PublicationStage, string> = {
  proposed: "Proposed",
  consultation: "Open for comment",
  adopted: "Adopted",
  in_force: "In force",
  repealed: "Repealed",
  unknown: "",
};

const APPLICABILITY: Record<
  PublicationApplicability,
  { readonly label: string; readonly className: string }
> = {
  relevant: { label: "Relevant", className: "bg-destructive/10 text-destructive" },
  not_applicable: { label: "Not applicable", className: "bg-muted text-muted-foreground" },
  needs_review: { label: "Needs review", className: "bg-warning/12 text-warning" },
};


/** The impact a publication has here, in one word, from the facts on it. */
export function publicationImpact(update: RegulatoryUpdate): {
  readonly label: string;
  readonly className: string;
} {
  const open = update.openFindings ?? 0;
  const total = update.totalFindings ?? 0;
  if (open > 0) {
    return {
      label: `${open} ${open === 1 ? "document" : "documents"}`,
      className: "bg-destructive/10 text-destructive",
    };
  }
  if (total > 0) return { label: "Resolved", className: "bg-success/10 text-success" };
  const applicability = update.applicability ?? "needs_review";
  const pill = APPLICABILITY[applicability];
  // The agent's word is a proposal until a person repeats it; say so on the pill.
  if (applicability !== "needs_review" && update.applicabilityDecidedByEmail == null) {
    return { label: `Proposed ${pill.label.toLowerCase()}`, className: pill.className };
  }
  return pill;
}


/* ------------------------------------------------------------------ */
/* Piles and filters                                                   */
/* ------------------------------------------------------------------ */

export const ALL = "__all__";
export type StatusFilter = typeof ALL | "decide" | "findings" | "relevant" | "not_applicable";
/** The status tabs, in the order the list's groups sit. */
export const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
  [ALL]: "All",
  findings: "Open findings",
  decide: "Needs decision",
  relevant: "Relevant",
  not_applicable: "Not applicable",
};
/** A framework the list is narrowed to, from a row of the frameworks table. */
export interface FrameworkFilter {
  readonly id: string;
  readonly name: string;
  readonly regimeIds: ReadonlyArray<string>;
}
export interface PublicationFilters {
  readonly framework: FrameworkFilter | null;
  readonly query: string;
  readonly status: StatusFilter;
  readonly source: string;
  readonly regime: string;
  readonly kind: string;
  readonly stage: string;
}
export const EMPTY_FILTERS: PublicationFilters = {
  framework: null,
  query: "",
  status: ALL,
  source: ALL,
  regime: ALL,
  kind: ALL,
  stage: ALL,
};
/** The filters folded behind the Filter button — not search, the status tab or the framework chip. */
export const refinementCount = (filters: PublicationFilters): number =>
  [filters.source, filters.regime, filters.kind, filters.stage].filter((value) => value !== ALL).length;

/** The source a publication is filed under, as the table names it. */
export const sourceOf = (update: RegulatoryUpdate): string =>
  (update.monitorName ?? update.jurisdictionName).split(" — ")[0] ?? "";

/** The list's groups — one per status tab, so a tab and its group always hold the same rows. */
export type PublicationSection = Exclude<StatusFilter, typeof ALL>;
export const SECTION_ORDER: ReadonlyArray<PublicationSection> = ["findings", "decide", "relevant", "not_applicable"];
/** The group a state files under: the agent's "not applicable" sits with a person's, ahead of it. */
export function sectionOf(state: PublicationState): PublicationSection {
  switch (state) {
    case "findings":
      return "findings";
    case "decide":
      return "decide";
    case "handled":
      return "relevant";
    case "not_applicable":
    case "proposed_not_applicable":
      return "not_applicable";
  }
}
/** The status glyph a group's header carries. */
export const SECTION_STATE: Record<PublicationSection, PublicationState> = {
  findings: "findings",
  decide: "decide",
  relevant: "handled",
  not_applicable: "not_applicable",
};

const matchesStatus = (update: RegulatoryUpdate, status: StatusFilter): boolean =>
  status === ALL || sectionOf(publicationState(update)) === status;

/** Real filtering over the set, in the Policies shelf's grammar. */
export function filterPublications(
  updates: ReadonlyArray<RegulatoryUpdate>,
  filters: PublicationFilters,
): RegulatoryUpdate[] {
  const needle = filters.query.trim().toLowerCase();
  return updates.filter(
    (update) =>
      matchesStatus(update, filters.status) &&
      (filters.framework === null || filedUnder(update, filters.framework.regimeIds)) &&
      (filters.source === ALL || sourceOf(update) === filters.source) &&
      (filters.regime === ALL || (update.frameworkName ?? "") === filters.regime) &&
      (filters.kind === ALL || (update.kind ?? "other") === filters.kind) &&
      (filters.stage === ALL || (update.stage ?? "unknown") === filters.stage) &&
      (needle.length === 0 ||
        update.title.toLowerCase().includes(needle) ||
        (update.summary ?? "").toLowerCase().includes(needle) ||
        sourceOf(update).toLowerCase().includes(needle) ||
        (update.frameworkName ?? "").toLowerCase().includes(needle)),
  );
}

/**
 * The list's rows: runs of notices folded first, then filed under the status
 * of their lead, the way the All tab groups them. A run that mixes statuses is
 * one row in one tab, never a row in two.
 */
export function publicationRows(
  updates: ReadonlyArray<RegulatoryUpdate>,
  filters: PublicationFilters,
): PublicationGroup[] {
  const groups = groupPublications(filterPublications(updates, { ...filters, status: ALL }));
  return filters.status === ALL ? groups : groups.filter((group) => matchesStatus(group.lead, filters.status));
}

/** How many rows each status tab would show under the rest of the filters. */
export function statusTabCounts(
  updates: ReadonlyArray<RegulatoryUpdate>,
  filters: PublicationFilters,
): Record<StatusFilter, number> {
  const groups = publicationRows(updates, { ...filters, status: ALL });
  const counts = {} as Record<StatusFilter, number>;
  for (const status of Object.keys(STATUS_FILTER_LABELS) as StatusFilter[]) {
    counts[status] = groups.filter((group) => matchesStatus(group.lead, status)).length;
  }
  return counts;
}

/** Publications per week by the issuer's date, split by whether they touch this firm. */
export interface WeekPoint {
  readonly label: string;
  readonly relevant: number;
  readonly other: number;
}
const touches = (update: RegulatoryUpdate): boolean =>
  update.applicability === "relevant" || (update.openFindings ?? 0) > 0;
export function weeklySeries(
  updates: ReadonlyArray<RegulatoryUpdate>,
  now: number,
  weeks = 12,
): WeekPoint[] {
  const points: WeekPoint[] = [];
  for (let index = weeks - 1; index >= 0; index -= 1) {
    const end = now - index * 7 * DAY;
    const start = end - 7 * DAY;
    let relevant = 0;
    let other = 0;
    for (const update of updates) {
      const at = Date.parse(update.publishedAt ?? update.detectedAt);
      if (at > start && at <= end) {
        if (touches(update)) relevant += 1;
        else other += 1;
      }
    }
    points.push({
      label: new Date(start + DAY).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      relevant,
      other,
    });
  }
  return points;
}

/* ------------------------------------------------------------------ */
/* The publications list                                               */
/* ------------------------------------------------------------------ */

export const STATE_LABELS: Record<PublicationState, string> = {
  findings: "Open findings",
  decide: "Needs decision",
  handled: "Relevant",
  proposed_not_applicable: "Proposed not applicable",
  not_applicable: "Not applicable",
};
/** Groups a reader skims past start folded; their header still carries the count. */
export const FOLDED_SECTIONS: ReadonlySet<PublicationSection> = new Set(["not_applicable"]);

const leadAt = (group: PublicationGroup): number =>
  Date.parse(group.lead.publishedAt ?? group.lead.detectedAt);
/** Within "not applicable", the agent's proposals (still open to a person) come first. */
const proposedFirst = (group: PublicationGroup): number =>
  publicationState(group.lead) === "proposed_not_applicable" ? 0 : 1;

/**
 * Rows filed under their tab's group in reading order, most open findings
 * then newest first within; empty groups dropped.
 */
export function groupBySection(
  groups: ReadonlyArray<PublicationGroup>,
): Array<{ readonly section: PublicationSection; readonly groups: PublicationGroup[] }> {
  return SECTION_ORDER.map((section) => ({
    section,
    groups: groups
      .filter((group) => sectionOf(publicationState(group.lead)) === section)
      .sort(
        (a, b) =>
          (b.lead.openFindings ?? 0) - (a.lead.openFindings ?? 0) ||
          proposedFirst(a) - proposedFirst(b) ||
          leadAt(b) - leadAt(a),
      ),
  })).filter((entry) => entry.groups.length > 0);
}

/** A list date as Linear prints one: "Sep 14", with the year only when it is not this one. */
export function shortDate(value: string | null | undefined, now: number): string {
  if (!value) return "—";
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return "—";
  const sameYear = at.getFullYear() === new Date(now).getFullYear();
  return at.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/** Who has said whether a publication applies here, in a few words. */
function decisionLine(update: RegulatoryUpdate): string {
  const applicability = update.applicability ?? "needs_review";
  if (applicability === "needs_review") return "Nobody has said whether this applies yet";
  const verdict = APPLICABILITY[applicability].label.toLowerCase();
  return update.applicabilityDecidedByEmail
    ? `${update.applicabilityDecidedByEmail} marked it ${verdict}`
    : `The agent proposes ${verdict}`;
}

/** The decision a person owes the publication, as the peek's footer. */
function PublicationDecision({
  update,
  canManage,
  busy,
  onDecide,
  onReject,
}: {
  readonly update: RegulatoryUpdate;
  readonly canManage: boolean;
  readonly busy: boolean;
  readonly onDecide: (update: RegulatoryUpdate, applicability: PublicationApplicability) => void;
  readonly onReject: (update: RegulatoryUpdate) => void;
}) {
  const applicability = update.applicability ?? "needs_review";
  const proposed = update.applicabilityDecidedByEmail == null && applicability !== "needs_review";
  return (
    <SheetFooter className="items-center gap-3 sm:justify-between">
      <span className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
        <DeciderAvatar update={update} />
        <span className="truncate">{decisionLine(update)}</span>
      </span>
      {canManage ? (
        <span className="flex shrink-0 items-center gap-1.5">
          {applicability !== "not_applicable" || proposed ? (
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => onReject(update)}>
              <XIcon aria-hidden />
              {proposed && applicability === "not_applicable" ? "Confirm not applicable" : "Not applicable"}
            </Button>
          ) : null}
          {applicability !== "relevant" || proposed ? (
            <Button type="button" size="sm" disabled={busy} onClick={() => onDecide(update, "relevant")}>
              <CheckIcon aria-hidden />
              {proposed && applicability === "relevant" ? "Confirm relevant" : "Relevant"}
            </Button>
          ) : null}
        </span>
      ) : null}
    </SheetFooter>
  );
}

/** What the peek says under its properties: why, what it touches, the notices, the summary. */
function PublicationDetail({
  group,
  policyNames,
  orgSegment,
}: {
  readonly group: PublicationGroup;
  readonly policyNames: ReadonlyMap<string, string>;
  readonly orgSegment: string;
}) {
  const update = group.lead;
  const affected = update.affectedPolicyIds.flatMap((id) => {
    const name = policyNames.get(id);
    return name === undefined ? [] : [{ id, name }];
  });
  const source = safeExternalHref(update.sourceUrl);
  return (
    <div className="space-y-5 whitespace-normal text-[13px]">
      {affected.length > 0 ? (
        <section className="space-y-2">
          <h3 className="text-xs font-medium text-muted-foreground">Touches</h3>
          <div className="flex flex-wrap gap-1.5">
            {affected.map((document) => (
              <Link
                key={document.id}
                to="/$org/compliance/policies/$policyId"
                params={{ org: orgSegment, policyId: document.id }}
                className="inline-flex h-6 max-w-full items-center gap-1.5 rounded-md border border-border/80 px-2 text-xs text-foreground/90 transition-colors hover:bg-foreground/[0.04]"
              >
                <FileTextIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{document.name}</span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
      {update.applicabilityReason ? (
        <section className="space-y-1.5">
          <h3 className="text-xs font-medium text-muted-foreground">
            {update.applicabilityDecidedByEmail ? "Why it was decided" : "Why the agent says so"}
          </h3>
          <p className="leading-relaxed text-foreground/85">{update.applicabilityReason}</p>
        </section>
      ) : null}
      {update.summary ? (
        <section className="space-y-1.5">
          <h3 className="text-xs font-medium text-muted-foreground">Summary</h3>
          <p className="whitespace-pre-line leading-relaxed text-foreground/85">{update.summary}</p>
        </section>
      ) : null}
      {group.items.length > 1 ? (
        <section className="space-y-1.5">
          <h3 className="text-xs font-medium text-muted-foreground">{group.items.length} notices with this title</h3>
          <ul className="divide-y divide-border/50 rounded-lg border border-border/60">
            {group.items.map((item) => {
              const href = safeExternalHref(item.sourceUrl);
              return (
                <li key={item.id} className="flex h-8 items-center justify-between gap-3 px-3 text-xs">
                  <span className="tabular-nums text-foreground/85">{formatDate(item.publishedAt ?? item.detectedAt)}</span>
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                    >
                      Source
                      <ExternalLinkIcon aria-hidden className="size-3" />
                    </a>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : source ? (
        <a
          href={source}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ExternalLinkIcon aria-hidden className="size-3.5" />
          Read it at the source
        </a>
      ) : null}
    </div>
  );
}

function PublicationRow({
  group,
  now,
  onOpen,
}: {
  readonly group: PublicationGroup;
  readonly now: number;
  readonly onOpen: () => void;
}) {
  const update = group.lead;
  const findings = update.openFindings ?? 0;
  const impact = impactLevel(update);
  const kind = KIND_LABELS[update.kind ?? "other"];
  const date = update.publishedAt ?? update.detectedAt;
  return (
    <li>
      <button
        type="button"
        data-publication-row={group.key}
        onClick={onOpen}
        className="flex h-11 w-full min-w-0 cursor-pointer items-center gap-2.5 bg-card pe-4 ps-3 text-left text-[13px] outline-none transition-colors duration-150 hover:bg-foreground/[0.035] focus-visible:bg-foreground/[0.05] sm:h-10 sm:pe-6 sm:ps-4"
      >
        <span title={IMPACT_LABELS[impact]} className="flex shrink-0">
          <ImpactIcon level={impact} />
        </span>
        <span className="w-12 shrink-0 truncate text-[12px] tabular-nums text-muted-foreground max-sm:hidden">
          {sourceOf(update)}
        </span>
        <span title={publicationImpact(update).label} className="flex shrink-0">
          <StatusIcon state={publicationState(update)} />
        </span>
        <span className="min-w-0 truncate font-medium text-foreground" title={update.title}>
          {update.title}
        </span>
        {group.items.length > 1 ? (
          <span
            title={`${group.items.length} notices with this title`}
            className="shrink-0 text-xs tabular-nums text-muted-foreground"
          >
            {group.items.length}
          </span>
        ) : null}
        <span className="ms-auto flex shrink-0 items-center gap-1.5 ps-2">
          {findings > 0 ? (
            <LabelTag
              name={`${findings} ${findings === 1 ? "document" : "documents"}`}
              color="var(--color-destructive)"
              className="max-sm:hidden"
            />
          ) : null}
          <LabelTag name={kind} className="hidden xl:inline-flex" />
          {update.frameworkName ? (
            <LabelTag
              name={update.frameworkName}
              icon={<LandmarkIcon aria-hidden className="size-3 shrink-0" />}
              className="hidden max-w-52 lg:inline-flex"
            />
          ) : null}
          <span className="flex size-5 items-center justify-center">
            <DeciderAvatar update={update} />
          </span>
          <time
            dateTime={date}
            title={formatDate(date)}
            className="w-12 text-right text-xs tabular-nums text-muted-foreground"
          >
            {shortDate(date, now)}
          </time>
        </span>
      </button>
    </li>
  );
}

/** Linear's peek: the publication opened beside the list — properties, then the detail, the decision pinned below. */
function PublicationPeek({
  group,
  onClose,
  policyNames,
  orgSegment,
  ...decision
}: {
  readonly group: PublicationGroup | null;
  readonly onClose: () => void;
  readonly policyNames: ReadonlyMap<string, string>;
  readonly orgSegment: string;
  readonly canManage: boolean;
  readonly busy: boolean;
  readonly onDecide: (update: RegulatoryUpdate, applicability: PublicationApplicability) => void;
  readonly onReject: (update: RegulatoryUpdate) => void;
}) {
  const update = group?.lead ?? null;
  const state = update ? publicationState(update) : null;
  const impact = update ? impactLevel(update) : null;
  const stage = update ? STAGE_LABELS[update.stage ?? "unknown"] : "";
  return (
    <Sheet open={group !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <SheetPopup className="w-full max-w-xl">
        {group && update && state && impact ? (
          <>
            <SheetHeader className="gap-2.5 pb-4">
              <p className="flex items-center gap-1.5 pe-8 text-xs text-muted-foreground">
                <span className="font-medium text-foreground/80">{sourceOf(update)}</span>
                <ChevronRightIcon aria-hidden className="size-3" />
                <StatusIcon state={state} />
                <span>{STATE_LABELS[state]}</span>
              </p>
              <SheetTitle className="pe-6 text-lg leading-snug font-semibold">{update.title}</SheetTitle>
            </SheetHeader>
            <SheetPanel className="space-y-6">
              <dl className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-2.5 text-[13px]">
                <dt className="text-muted-foreground">Status</dt>
                <dd className="flex items-center gap-2 text-foreground/90">
                  <StatusIcon state={state} />
                  {STATE_LABELS[state]}
                  {(update.openFindings ?? 0) > 0 ? (
                    <span className="text-muted-foreground">· {publicationImpact(update).label}</span>
                  ) : null}
                </dd>
                <dt className="text-muted-foreground">Impact</dt>
                <dd className="flex items-center gap-2 text-foreground/90">
                  <ImpactIcon level={impact} />
                  {IMPACT_LABELS[impact].split(" — ")[0]}
                </dd>
                {update.frameworkName ? (
                  <>
                    <dt className="text-muted-foreground">Regime</dt>
                    <dd className="flex min-w-0 items-center gap-2 text-foreground/90">
                      <LandmarkIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{update.frameworkName}</span>
                    </dd>
                  </>
                ) : null}
                <dt className="text-muted-foreground">Type</dt>
                <dd className="text-foreground/90">
                  {KIND_LABELS[update.kind ?? "other"]}
                  {stage ? <span className="text-muted-foreground"> · {stage}</span> : null}
                </dd>
                <dt className="text-muted-foreground">Published</dt>
                <dd className="tabular-nums text-foreground/90">{formatDate(update.publishedAt ?? update.detectedAt)}</dd>
                {update.effectiveAt ? (
                  <>
                    <dt className="text-muted-foreground">In force</dt>
                    <dd className="tabular-nums text-foreground/90">{formatDate(update.effectiveAt)}</dd>
                  </>
                ) : null}
                {update.commentDeadlineAt ? (
                  <>
                    <dt className="text-muted-foreground">Comments close</dt>
                    <dd className="tabular-nums text-foreground/90">{formatDate(update.commentDeadlineAt)}</dd>
                  </>
                ) : null}
              </dl>
              <PublicationDetail group={group} policyNames={policyNames} orgSegment={orgSegment} />
            </SheetPanel>
            <PublicationDecision update={update} {...decision} />
          </>
        ) : null}
      </SheetPopup>
    </Sheet>
  );
}

/** j/k and the arrow keys walk the rows, the way Linear's list does. */
function moveRowFocus(event: KeyboardEvent<HTMLElement>) {
  const forward = event.key === "ArrowDown" || event.key === "j";
  const backward = event.key === "ArrowUp" || event.key === "k";
  if (!forward && !backward) return;
  const rows = [...event.currentTarget.querySelectorAll<HTMLButtonElement>("[data-publication-row]")];
  if (rows.length === 0) return;
  const at = rows.indexOf(document.activeElement as HTMLButtonElement);
  const next = at < 0 ? 0 : Math.min(rows.length - 1, Math.max(0, at + (forward ? 1 : -1)));
  event.preventDefault();
  rows[next]?.focus();
}

/* ------------------------------------------------------------------ */
/* The page                                                            */
/* ------------------------------------------------------------------ */

const WEEK_SERIES = [
  { key: "other", label: "Other", color: "var(--color-muted-foreground)", top: 0.28 },
  { key: "relevant", label: "Touches this firm", color: "var(--color-destructive)", top: 0.55 },
] as const;

/**
 * Two stacked areas, drawn to the card's edges: no box, no grid, no y-axis —
 * the shape is the reading. Gradients fade each area into the card so the
 * chart is part of the surface rather than a widget sitting on it.
 */
export function WeeklyChart({ data }: { readonly data: ReadonlyArray<WeekPoint> }) {
  // Every third week, skipping the edge columns whose labels the card clips.
  const ticks = data
    .filter((_, index) => index % 3 === 2 && index < data.length - 1)
    .map((point) => point.label);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={[...data]} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
        <defs>
          {WEEK_SERIES.map((series) => (
            <linearGradient key={series.key} id={`monitor-week-${series.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={series.color} stopOpacity={series.top} />
              <stop offset="100%" stopColor={series.color} stopOpacity={0.02} />
            </linearGradient>
          ))}
        </defs>
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          ticks={[...ticks]}
          height={20}
          tickMargin={4}
          tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
        />
        <Tooltip
          cursor={{ stroke: "var(--color-border)", strokeDasharray: "3 3" }}
          wrapperStyle={{ zIndex: 40 }}
          content={<ChartTooltipContent />}
        />
        {WEEK_SERIES.map((series) => (
          <Area
            key={series.key}
            name={series.label}
            dataKey={series.key}
            stackId="week"
            type="monotone"
            stroke={series.color}
            strokeWidth={series.key === "relevant" ? 1.75 : 1}
            strokeOpacity={series.key === "relevant" ? 1 : 0.5}
            fill={`url(#monitor-week-${series.key})`}
            isAnimationActive={false}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function FilterSelect<T extends string>({
  value,
  onChange,
  all,
  options,
  label,
  width,
}: {
  readonly value: T | typeof ALL;
  readonly onChange: (value: T | typeof ALL) => void;
  readonly all: string;
  readonly options: ReadonlyArray<{ readonly value: T; readonly label: string }>;
  readonly label: string;
  readonly width: string;
}) {
  const current = options.find((option) => option.value === value)?.label ?? all;
  return (
    <Select value={value as string} onValueChange={(next) => onChange((next ?? ALL) as T | typeof ALL)}>
      <SelectTrigger size="sm" className={width} aria-label={label}>
        {/* Render the label ourselves: the sentinel must never surface as "__all__". */}
        <span className="truncate">{current}</span>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{all}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** The page's frame: its own one-row header (as Findings has), then the scrolling body. */
function MonitorFrame({
  tabs,
  actions,
  children,
}: {
  readonly tabs?: ReactNode;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <div data-regulation-page="true" data-monitor-page="" className="relative flex min-h-0 flex-1 flex-col">
      <PageHeader
        title="Monitor"
        tabs={tabs}
        actions={
          <>
            {actions}
            <SourcesStatus />
          </>
        }
      />
      {children}
    </div>
  );
}

/** A group's band, as the Findings queue draws one: sticky, chevron, glyph, name, count. */
function GroupBand({
  label,
  count,
  icon,
  folded,
  onToggle,
  note,
}: {
  readonly label: string;
  readonly count: number;
  readonly icon: ReactNode;
  readonly folded: boolean;
  readonly onToggle: () => void;
  readonly note?: string | null;
}) {
  return (
    <div className="sticky top-0 z-10 flex h-9 items-center gap-2 border-b border-border/60 bg-muted/60 pe-4 ps-2.5 backdrop-blur sm:pe-6 sm:ps-3.5 dark:bg-muted/40">
      <button
        type="button"
        aria-expanded={!folded}
        onClick={onToggle}
        className="flex min-w-0 cursor-pointer items-center gap-2 rounded-md py-1 pe-1.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChevronDownIcon
          aria-hidden
          className={cn("size-3.5 shrink-0 text-muted-foreground transition-transform duration-150", folded && "-rotate-90")}
        />
        {icon}
        <span className="truncate font-medium">{label}</span>
        <span className="text-xs tabular-nums text-muted-foreground">{count}</span>
      </button>
      {note ? <span className="ms-auto truncate text-xs text-muted-foreground">{note}</span> : null}
    </div>
  );
}

export function MonitorSection() {
  const { org, isLoading } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const { canManage } = useOrgProfile(organizationId);
  const monitors = useMonitors(organizationId);
  const frameworks = useGovernanceFrameworks(organizationId);
  const updates = useRegulatoryUpdates(organizationId);
  const catalogue = useFrameworks(organizationId);
  const { snapshot: library } = usePolicies(organizationId);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // The open peek: its run, and the notice that led the row it was opened from.
  const [opened, setOpened] = useState<{ readonly key: string; readonly leadId: string } | null>(null);
  const [folded, setFolded] = useState<ReadonlySet<string>>(FOLDED_SECTIONS);
  // The tenant segment comes from the URL this page lives under, so a link can
  // never cross into a different organization.
  const orgSegment = useParams({
    strict: false,
    select: (params) => (params as { org?: string }).org ?? "",
  });
  const [filters, setFilters] = useState<PublicationFilters>(EMPTY_FILTERS);
  const [rejecting, setRejecting] = useState<RegulatoryUpdate | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement | null>(null);
  // `#publication-<id>` (publicationLink) opens that publication's peek, once
  // per link, as soon as the publications have loaded.
  const linkedId = publicationIdFromHash(useLocation({ select: (location) => location.hash }));
  const openedLink = useRef<string | null>(null);
  useEffect(() => {
    if (linkedId === null || openedLink.current === linkedId || !updates.snapshot) return;
    const group = groupPublications(updates.snapshot.updates).find((candidate) =>
      candidate.items.some((update) => update.id === linkedId),
    );
    if (!group) return;
    openedLink.current = linkedId;
    setFilters(EMPTY_FILTERS);
    setOpened({ key: group.key, leadId: group.lead.id });
  }, [linkedId, updates.snapshot]);
  // One clock reading per snapshot, so every "checked N days ago" agrees.
  const now = useMemo(() => Date.now(), [monitors.snapshot, updates.snapshot, frameworks.snapshot]);
  // Controls addressed per regime, for each framework's coverage.
  const coverage = useMemo(
    () =>
      new Map(
        (catalogue.snapshot?.frameworks ?? []).map((regime): [string, RegimeCoverage] => [regime.id, regime]),
      ),
    [catalogue.snapshot],
  );

  const policyNames = useMemo(
    () =>
      new Map((library?.policies ?? []).map((policy) => [policy.id, policyDisplayName(policy)])),
    [library],
  );

  const run = async (action: () => Promise<string | null>) => {
    setBusy(true);
    setMessage(null);
    const error = await action();
    setBusy(false);
    setMessage(error);
    return error;
  };
  const patch = (change: Partial<PublicationFilters>) =>
    setFilters((current) => ({ ...current, ...change }));
  const toggleFold = (key: string) =>
    setFolded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const blocked =
    org === null
      ? isLoading
        ? "Loading your organizations…"
        : "Choose an organization from the switcher to see its monitor."
      : monitors.unavailableReason !== null
        ? UNAVAILABLE_MESSAGES[monitors.unavailableReason]
        : null;
  if (blocked !== null) {
    return (
      <MonitorFrame>
        <p className="mx-4 mt-8 rounded-xl border border-dashed px-6 py-10 text-center text-sm text-muted-foreground sm:mx-6">
          {blocked}
        </p>
      </MonitorFrame>
    );
  }

  const watched = monitors.snapshot?.monitors ?? [];
  const enabledCount = watched.filter((monitor) => monitor.enabled).length;
  const frameworkList = frameworks.snapshot?.frameworks ?? [];
  const unorganisedRegimes = frameworks.snapshot?.unorganisedRegimes ?? [];
  const publications = [...(updates.snapshot?.updates ?? [])].sort(
    (a, b) =>
      Date.parse(b.publishedAt ?? b.detectedAt) - Date.parse(a.publishedAt ?? a.detectedAt),
  );
  // The filter options come from the set itself: nothing offered that no row has.
  const distinct = <K,>(pick: (update: RegulatoryUpdate) => K | null | undefined): K[] =>
    Array.from(new Set(publications.map(pick).filter((value): value is K => value != null && value !== "")));
  const sources = distinct(sourceOf).sort();
  const regimes = distinct((update) => update.frameworkName).sort();
  const kinds = distinct((update) => update.kind ?? "other");
  const stages = distinct((update) => update.stage ?? "unknown").filter((stage) => STAGE_LABELS[stage]);
  const refinements = refinementCount(filters);
  const tabCounts = statusTabCounts(publications, filters);
  // Rows fold runs of notices; the list files them under their tab's group (`groupBySection`).
  const groups = publicationRows(publications, filters);
  const upcoming = publications
    .filter((update) => update.effectiveAt && Date.parse(update.effectiveAt) >= now)
    .sort((a, b) => Date.parse(a.effectiveAt ?? "") - Date.parse(b.effectiveAt ?? ""))
    .slice(0, 3);
  // The peek follows its run whatever the list shows: deciding it can move it
  // out of the open tab, and the filters may hide it, but the sheet stays.
  // A filtered row can lead with another notice than the whole run; the peek
  // keeps the one that was clicked, read fresh after a decision.
  const openRun = opened === null ? undefined : groupPublications(publications).find((group) => group.key === opened.key);
  const peeked: PublicationGroup | null = openRun
    ? { ...openRun, lead: openRun.items.find((update) => update.id === opened?.leadId) ?? openRun.lead }
    : null;
  const tabs = (Object.keys(STATUS_FILTER_LABELS) as StatusFilter[]).filter(
    // A tab that would show nothing stays out of the way, unless it is the one you are on.
    (status) => status === ALL || tabCounts[status] > 0 || filters.status === status,
  );
  // Regimes in no framework, and (for a manager) the action that files them.
  const organiseNote =
    unorganisedRegimes.length > 0 ? (
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate">
          {unorganisedRegimes.length} {unorganisedRegimes.length === 1 ? "regime sits" : "regimes sit"} in no
          framework: {unorganisedRegimes.map((regime) => regime.name).join(", ")}
        </span>
        {canManage ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(() => frameworks.propose())}
            className="shrink-0 cursor-pointer font-medium text-foreground underline-offset-2 hover:underline"
            title="One framework per domain of the regimes you enabled; rename, merge or split afterwards."
          >
            Organise
          </button>
        ) : null}
      </span>
    ) : null;

  return (
    <MonitorFrame
      tabs={
        publications.length > 0 ? (
          <SegmentedTabs
            label="Publication status"
            value={filters.status}
            onValueChange={(status) => patch({ status })}
            tabs={tabs.map((status) => ({ value: status, label: STATUS_FILTER_LABELS[status], count: tabCounts[status] }))}
          />
        ) : undefined
      }
      actions={
        publications.length > 0 ? (
          <>
            {filters.framework ? (
              <span className="inline-flex h-6 max-w-48 items-center gap-1 rounded-md border border-border bg-foreground/[0.04] ps-2 pe-0.5 text-xs font-medium text-foreground/90">
                <LayersIcon aria-hidden className="size-3 shrink-0 text-muted-foreground" />
                <span className="truncate">{filters.framework.name}</span>
                <button
                  type="button"
                  aria-label="Show every framework"
                  onClick={() => patch({ framework: null })}
                  className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                >
                  <XIcon className="size-3" />
                </button>
              </span>
            ) : null}
            {filters.query.trim() ? (
              <Button
                size="xs"
                variant="secondary"
                className="max-w-40"
                onClick={() => setFilterOpen(true)}
                aria-label={`Search: ${filters.query}`}
              >
                <SearchIcon aria-hidden />
                <span className="truncate">{filters.query}</span>
              </Button>
            ) : null}
            {
              <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                <PopoverTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="xs"
                      aria-label={`Filter${refinements ? `, ${refinements} active` : ""}`}
                    />
                  }
                >
                  <ListFilterIcon aria-hidden />
                  <span className="max-sm:hidden">Filter</span>
                  {refinements > 0 ? (
                    <Badge variant="secondary" size="sm" className="tabular-nums">
                      {refinements}
                    </Badge>
                  ) : null}
                </PopoverTrigger>
                <PopoverPopup
                  align="end"
                  className="w-72"
                  viewportClassName="p-0 [--viewport-inline-padding:0px]"
                  initialFocus={searchRef}
                >
                  <label className="flex h-10 items-center gap-2 border-b border-border/70 px-3">
                    <SearchIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
                    <input
                      ref={searchRef}
                      value={filters.query}
                      onChange={(event) => patch({ query: event.currentTarget.value })}
                      placeholder="Search publications"
                      aria-label="Search publications"
                      className="min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/70"
                    />
                    {filters.query ? (
                      <button
                        type="button"
                        aria-label="Clear search"
                        onClick={() => patch({ query: "" })}
                        className="cursor-pointer rounded-sm text-muted-foreground hover:text-foreground"
                      >
                        <XIcon className="size-3.5" />
                      </button>
                    ) : null}
                  </label>
                  <div className="space-y-2 p-3">
                    {sources.length > 1 ? (
                      <FilterSelect
                        value={filters.source}
                        onChange={(source) => patch({ source })}
                        all="All sources"
                        label="Filter by source"
                        width="w-full"
                        options={sources.map((source) => ({ value: source, label: source }))}
                      />
                    ) : null}
                    {regimes.length > 1 ? (
                      <FilterSelect
                        value={filters.regime}
                        onChange={(regime) => patch({ regime })}
                        all="All regimes"
                        label="Filter by regime"
                        width="w-full"
                        options={regimes.map((regime) => ({ value: regime, label: regime }))}
                      />
                    ) : null}
                    {kinds.length > 1 ? (
                      <FilterSelect
                        value={filters.kind}
                        onChange={(kind) => patch({ kind })}
                        all="All types"
                        label="Filter by type"
                        width="w-full"
                        options={kinds.map((kind) => ({ value: kind, label: KIND_LABELS[kind] }))}
                      />
                    ) : null}
                    {stages.length > 1 ? (
                      <FilterSelect
                        value={filters.stage}
                        onChange={(stage) => patch({ stage })}
                        all="All stages"
                        label="Filter by stage"
                        width="w-full"
                        options={stages.map((stage) => ({ value: stage, label: STAGE_LABELS[stage] ?? stage }))}
                      />
                    ) : null}
                  </div>
                  <div className="flex items-center justify-between border-t p-2">
                    <Button
                      variant="ghost"
                      size="xs"
                      disabled={refinements === 0 && filters.query === ""}
                      onClick={() => patch({ query: "", source: ALL, regime: ALL, kind: ALL, stage: ALL })}
                    >
                      Clear
                    </Button>
                    <PopoverClose render={<Button variant="outline" size="xs" />}>Done</PopoverClose>
                  </div>
                </PopoverPopup>
              </Popover>
            }
          </>
        ) : undefined
      }
    >
      <RejectDialog
        open={rejecting !== null}
        onOpenChange={(open) => {
          if (!open) setRejecting(null);
        }}
        what="publication"
        title={rejecting?.title ?? null}
        allowProof={false}
        onReject={async ({ reason }) => {
          if (rejecting === null) return "Nothing to decide.";
          const error = await run(() =>
            updates.decideApplicability(rejecting.id, "not_applicable", reason),
          );
          if (error === null) setRejecting(null);
          return error;
        }}
      />
      <PublicationPeek
        group={peeked}
        onClose={() => setOpened(null)}
        policyNames={policyNames}
        orgSegment={orgSegment}
        canManage={canManage}
        busy={busy}
        onDecide={(update, applicability) =>
          void run(() => updates.decideApplicability(update.id, applicability, null))
        }
        onReject={(update) => setRejecting(update)}
      />
      <div onKeyDown={moveRowFocus} className="min-h-0 flex-1 overflow-y-auto pb-24" aria-label="Monitor">
        {message !== null ? (
          <p className="border-b border-border/60 px-4 py-2 text-xs text-destructive sm:px-6">{message}</p>
        ) : null}

        {/* Frameworks first: where the heat is. A row narrows the publications below.
            With none yet, the band still offers to organise the enabled regimes. */}
        {frameworkList.length > 0 || unorganisedRegimes.length > 0 ? (
          <section aria-label="Frameworks">
            <GroupBand
              label="Frameworks"
              count={frameworkList.length}
              icon={<LayersIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />}
              folded={folded.has("frameworks")}
              onToggle={() => toggleFold("frameworks")}
              note={filters.framework || frameworkList.length === 0 ? null : "Select one to narrow the publications"}
            />
            {folded.has("frameworks") ? null : frameworkList.length > 0 ? (
              <FrameworksTable
                flush
                frameworks={frameworkList}
                coverage={coverage}
                publications={publications}
                now={now}
                orgSegment={orgSegment}
                selectedId={filters.framework?.id ?? null}
                onSelect={(framework) =>
                  patch({
                    framework: framework
                      ? { id: framework.id, name: framework.name, regimeIds: framework.regimes.map((regime) => regime.id) }
                      : null,
                  })
                }
                footer={organiseNote}
              />
            ) : (
              <div className="flex min-h-9 items-center border-b border-border/50 bg-card py-2 pe-4 ps-11 text-xs text-muted-foreground sm:pe-6 sm:ps-12">
                {organiseNote}
              </div>
            )}
          </section>
        ) : null}

        {upcoming.length > 0 ? (
          <p className="flex h-9 min-w-0 items-center gap-x-4 overflow-hidden border-b border-border/60 bg-card px-4 text-xs whitespace-nowrap text-muted-foreground sm:px-6">
            <span className="flex shrink-0 items-center gap-1.5 font-medium text-foreground/90">
              <CalendarClockIcon aria-hidden className="size-3.5 text-muted-foreground" />
              Coming into force
            </span>
            {upcoming.map((update) => (
              <span key={update.id} className="min-w-0 truncate">
                <span className="tabular-nums text-foreground/90">{formatDate(update.effectiveAt)}</span> · {update.title}
              </span>
            ))}
          </p>
        ) : null}

        {updates.unavailableReason !== null ? (
          <p className="px-6 py-16 text-center text-sm text-muted-foreground">
            {UNAVAILABLE_MESSAGES[updates.unavailableReason]}
          </p>
        ) : publications.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-20 text-center">
            {updates.isPending && updates.snapshot === null ? null : (
              <HairlineArt figure={Dish} className="mb-3 w-48 sm:w-56" />
            )}
            <p className="text-[13px] font-medium text-foreground">
              {updates.isPending && updates.snapshot === null ? "Loading…" : "Nothing recorded yet"}
            </p>
            <p className="max-w-sm text-[13px] text-muted-foreground">
              {enabledCount > 0
                ? "The weekly inspection reads every watched source and records what the regulators published."
                : "Watch a source from the Sources menu above; the weekly inspection reads it and records what was published."}
            </p>
          </div>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-20 text-center">
            <p className="text-[13px] text-muted-foreground">No publications match</p>
            <Button size="xs" variant="outline" onClick={() => setFilters(EMPTY_FILTERS)}>
              Clear filters
            </Button>
          </div>
        ) : (
          groupBySection(groups).map(({ section, groups: rows }) => {
            const isFolded = folded.has(section);
            const proposed =
              section === "not_applicable"
                ? rows.filter((group) => publicationState(group.lead) === "proposed_not_applicable").length
                : 0;
            return (
              <section key={section} aria-label={STATUS_FILTER_LABELS[section]}>
                <GroupBand
                  label={STATUS_FILTER_LABELS[section]}
                  count={rows.length}
                  icon={<StatusIcon state={SECTION_STATE[section]} />}
                  folded={isFolded}
                  onToggle={() => toggleFold(section)}
                  note={proposed > 0 ? `${proposed} proposed by the agent, not yet confirmed` : null}
                />
                {isFolded ? null : (
                  <ul className="divide-y divide-border/50">
                    {rows.map((group) => (
                      <PublicationRow
                        key={group.key}
                        group={group}
                        now={now}
                        onOpen={() => setOpened({ key: group.key, leadId: group.lead.id })}
                      />
                    ))}
                  </ul>
                )}
              </section>
            );
          })
        )}
      </div>
    </MonitorFrame>
  );
}
